import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, Trophy, Settings, FlaskConical, Lock } from 'lucide-react';
import { getTeamLogoUrl, applyHeadToHeadTiebreakers, getWins } from '../utils/helpers';
import { logos } from '../teams/logos';
import { ratings26 } from '../ratings/ratings26';
import { oddsData } from '../ratings/odds';
import { hfaData } from '../hfa';
import Schedule from './schedule';
import ScenarioEditor from './ScenarioEditor';

const WHITE_LOGO = new Set([
    'Eastern Michigan', 'Georgia Tech', 'Kansas State', 'Nebraska', 'Oklahoma', 'TCU', 'Washington State', 'SMU'
]);

const ALT_COLOR = new Set([
    'Colorado', '', 'Miami', 'Missouri', 'North Dakota State', 'Ohio State', 'Oklahoma State', 'Syracuse', 'Texas Tech', 'Tulane', 'UAB', 'UCLA', 'UL Monroe', 'USC', 'Wake Forest'
]);

const REG_LOGO = new Set([
    'Colorado', "Marshall", 'Nebraska', 'Ohio State', 'Oklahoma State', 'Syracuse', 'UAB', 'UCLA', 'UL Monroe', 'UTEP', 'West Virginia', 'Western Kentucky'
]);


const LeaderboardTable = ({
    year = 2026,
    teams = [],
    teamOdds = {},
    listOfTeams = {},
    setSelectedTeam,
    week = 1,
    setWeek,
    onGameChange,
    games = [],
    setGames,
    setTeams,
    percent = {},
    records = {},
    grades = {},
    avgPositions = {},
    simResults = {},
    dark = false,
    logo_type = '',
    set_logo_type = null,
    setOddsFactor = null,
    mSwitch,
    setMSwitch
}) => {
    const VPR = false;

    const [wSwitch, setWSwitch] = useState(true);
    const [conferenceFilter, setConferenceFilter] = useState('Select Conference');
    const [showTip, setShowTip] = useState(true);
    const [gameList, setGameList] = useState([]);
    const [tab, setTab] = useState('odds');
    const [openAccordions, setOpenAccordions] = useState([]);
    const [openTeams, setOpenTeams] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const [sort, setSort] = useState("");
    const [filter, setFilter] = useState("all");
    const [max, setMax] = useState(year == 2026 ? 200 : 200);
    const sortedTeams = applyHeadToHeadTiebreakers(teams.sort(
        (a, b) => {
            if(year < 2026){
                return b.value - a.value;
            }
            if (Object.entries(grades).length > 0) {
                return grades?.[b.team] - grades?.[a.team];

            }
            return oddsData?.[b.team].resume - oddsData?.[a.team].resume;
        }), grades, 2);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 650px)");
        setIsMobile(mediaQuery.matches);
        const handleResize = (e) => setIsMobile(e.matches);
        mediaQuery.addEventListener("change", handleResize);
        return () => mediaQuery.removeEventListener("change", handleResize);
    }, []);

    const toggleAccordion = (index) => {
        setOpenAccordions((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index) 
                : [...prev, index]
        );
    };
    const toggleTeam = (teamName) => {
        setOpenTeams((prev) =>
            prev.includes(teamName)
                ? prev.filter((t) => t !== teamName)
                : [...prev, teamName]
        );
    };

    useEffect(() => {
        if (mSwitch) {
            document.querySelector('#scenario-editor')?.scrollIntoView({ behavior: 'smooth' });
        }
        else {
            document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' });

        }
    }, [mSwitch]);

    useEffect(() => {
        if (year < 2026) {
            setTab('ranking');
            setMSwitch(false);
        }
        else {
            setTab('odds');
        }
    }, [year]);

    useEffect(() => {
        if (!VPR) return;

        let isMounted = true;
        const fetchGames = async () => {
            const targetYear = 2026;
            const totalWeeks = 16;
            const promises = [];

            for (let w = 0; w <= totalWeeks; w++) {
                const url = `https://site.api.espn.com/apis/site/v2/sports/football/college-football/scoreboard?dates=${targetYear}&seasontype=2&week=${w}&groups=80&limit=500`;
                console.log(url);
                promises.push(
                    fetch(url)
                        .then((res) => res.json())
                        .then((data) => data.events || [])
                        .catch((err) => {
                            console.error(`Error fetching week ${w}:`, err);
                            return [];
                        })
                );
            }

            const results = await Promise.all(promises);
            if (isMounted) {
                console.log(results.flat());
                setGameList(results.flat());
            }
        };

        fetchGames();
        return () => {
            isMounted = false;
        };
    }, [VPR]);

    const handleChange = useCallback((gameId, winner, undoManual = false) => {
        const targetGame = games.find((g) => g.id === gameId);

        if (!targetGame) return;

        const updatedGame = { ...targetGame };

        if (undoManual) {
            updatedGame.manual = false;
            onGameChange(updatedGame);
            return;
        }

        updatedGame.disabled = false;

        if (winner === 'home') {
            updatedGame.homePoints = 1;
            updatedGame.awayPoints = 0;
            updatedGame.winner = updatedGame.homeTeam;
            updatedGame.manual = true;
        } else if (winner === 'away') {
            updatedGame.homePoints = 0;
            updatedGame.awayPoints = 1;
            updatedGame.winner = updatedGame.awayTeam;
            updatedGame.manual = true;
        } else {
            updatedGame.homePoints = null;
            updatedGame.awayPoints = null;
            updatedGame.winner = '';
            updatedGame.completed = false;
            updatedGame.manual = false;
        }

        onGameChange(updatedGame);
    }, [games, onGameChange]);

    const getFormattedPlayoffOdds = useCallback((teamName, data) => {
        const teamPercent = data[teamName];
        if (teamPercent === undefined || teamPercent === null) return '<1';

        if (teamPercent >= 1) {
            return Math.min(teamPercent, 100).toFixed(0);
        }
        return Math.max(1, teamPercent).toFixed(0);
    }, [percent]);

    const conferences = useMemo(() => {
        if (!sortedTeams?.length) return ['Select Conference'];
        const confSet = new Set(sortedTeams.map((t) => t.conference).filter(Boolean));
        return ['Select Conference', ...Array.from(confSet).sort((a, b) => a.localeCompare(b))];
    }, [sortedTeams]);

    const getWinner = (game) => {
        if (game.homePoints > game.awayPoints) return 'home';
        if (game.awayPoints > game.homePoints) return 'away';
        return null;
    };

    const processedTeams = useMemo(() => {
        if (!sortedTeams?.length) return [];

        const filtered = sortedTeams.filter((team) => {
            return team.conference === conferenceFilter || 'Select Conference' == conferenceFilter;
        });

        return filtered.sort((a, b) => {
            if (sort == "Resume" || mSwitch) {
                if (Object.entries(grades).length > 0) {
                    return grades?.[b.team] - grades?.[a.team];

                }
                return oddsData?.[b.team].resume - oddsData?.[a.team].resume;
            }
            if (sort == "Team") {
                return a.team.localeCompare(b.team);
            }
            if (sort == "Power") {
                return b.avg-a.avg;
            }
            if (sort == "OldRecord") {
                return (b.wins / b.gp + b.wins / 100 - b.gp / 200) - (a.wins / a.gp + a.wins / 100 - a.gp / 200);
            }
            if (sort == "OldResume") {
                return (oddsData[b.team].value) - (oddsData[a.team].value)
            }
            if (sort == "") {
                return true;
            }
            if (sort == "Odds") {
                if (Object.entries(percent).length > 0 ) {
                    return percent?.[b.team] - percent?.[a.team];

                }
                return oddsData?.[b.team].odds - oddsData?.[a.team].odds;
            }
            
            if (sort == "Record") {
                const winsA = getWins(records?.[a.team]);
                const winsB = getWins(records?.[b.team]);
                return winsB - winsA;
            }

            if (tab === 'ranking' || true) return 0;

            const pa = percent[a.team];
            const pb = percent[b.team];

            if (pa === pb) return (b.avg || 0) - (a.avg || 0);
            if (pa == null) return 1;
            if (pb == null) return -1;

            return pb - pa;
        });
    }, [sortedTeams, conferenceFilter, tab, percent, sort, filter]);

    if (!sortedTeams?.length) return null;

    const conferenceNames = {
        'Select Conference': 'CONF',
        'ACC': 'ACC',
        'Big Ten': 'B10',
        'Big 12': 'B12',
        'SEC': 'SEC',
        'American Athletic': 'AAC',
        'Conference USA': 'C-USA',
        'Mid-American': 'MAC',
        'Mountain West': 'MWC',
        'Sun Belt': 'SB',
        'Pac-12': 'P12',
        'FBS Independents': 'IND.'
    };

    return (
        <>
        <div className="leaderboard-table cfp">

            {/* VPR View */}
            {VPR && (
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Complete</th>
              <th>Favorite</th>
              <th>Underdog</th>
              <th>Spread</th>
            </tr>
          </thead>
          <tbody>
            {gameList.map((game, index) => {
              const comp = game.competitions?.[0];
              if (!comp) return null;

              const team1Loc = comp.competitors?.[0]?.team?.location;
              const team2Loc = comp.competitors?.[1]?.team?.location;
                let odds = parseFloat(comp.odds?.[0]?.spread) || null;
                
                const completed = game?.status?.type?.completed;
                if (odds == null && !completed) {
                    return;
                }
              const neutral = comp.neutralSite;

              const hfa = parseFloat(hfaData[team1Loc]) || 3;
              odds += neutral ? 0 : hfa;

              if (completed) {
                odds = (comp.competitors[1]?.score || 0) - (comp.competitors[0]?.score || 0) + hfa;
              }

              let team1 = team1Loc;
              let team2 = team2Loc;

              if (odds < 0) {
                odds = Math.abs(odds);
                team1 = team1Loc;
                team2 = team2Loc;
              } else {
                team1 = team2Loc;
                team2 = team1Loc;
              }

              if (
                !odds ||
                !team1 ||
                !team2 ||
                team1 === 'TBD' ||
                team1 === 'FCS' ||
                !ratings26[team1] ||
                team2 === 'TBD' ||
                team2 === 'FCS' ||
                !ratings26[team2]
              ) {
                return null;
              }

              return (
                <tr key={game.id || index}>
                  <td>{index + 1}</td>
                  <td>{completed ? '1' : '0'}</td>
                  <td>{team1}</td>
                  <td>{team2}</td>
                  <td>{odds.toFixed(1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

            {/* Main Content Area */}
            {mSwitch ? (
                    <ScenarioEditor games={games} change={handleChange} teams={teams.sort((a, b) => b.avg - a.avg)} oddsData={oddsData} simResults={simResults} setMSwitch={setMSwitch} isMobile={isMobile} />
             ) : (
            <table className="pp-teams">
                        <thead>
                            {year == 2026 && false && (
                                <tr className="pp-header">
                                    <th colSpan={3}></th>
                                    <th className="separator-left" colSpan={2}>Current</th>
                                    <th className="separator-left" colSpan={3}>Projections</th>
                                </tr>
                            )}
                            <tr >
                                {!mSwitch && (
                                    <>
                                        <th
                                            style={{ minWidth: '35px', cursor: 'pointer' }}
                                            className={`${sort === "" ? "active" : ""}`}
                                            onClick={() => setSort("")}
                                        >
                                            RK
                                        </th>

                                        <th
                                            style={{ textAlign: 'left', cursor: 'pointer', width:'170px' }}
                                            className={sort === "Team" ? "active" : ""}
                                            onClick={() => setSort("Team")}
                                        >
                                            Team
                                        </th>

                                        <th style={{ textAlign: 'center' }}
                                        >
                                                <select
                                                    id="conference-select"
                                                    aria-label="Select Conference"
                                                    value={conferenceFilter}
                                                    onChange={(e) => setConferenceFilter(e.target.value)}
                                                >

                                                    {conferences.map((conf, index) => (
                                                        <option key={index} value={conf}>
                                                            {conferenceNames[conf] ?? conf ?? 'N/A'}
                                                        </option>
                                                    ))}
                                                </select>
                                        </th>
                                            <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => year == 2027 ? null : setSort("OldRecord")}
                                                className={`${sort === "OldRecord" ? "active" : ""} separator`}>Record</th>

                                            {/*<th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => year == 2027 ? null : setSort("OldResume")}
                                                className={`${sort === "OldResume" ? "active" : ""} separator`}>Resume</th>*/}

                                            {!isMobile && <th style={{ textAlign: 'center', cursor: (year == 2027 ? 'default' : 'pointer') }} className={`${sort === "Power" ? "active" : ""}`} onClick={() => year == 2027 ? null : setSort("Power")}
                                            >
                                                Power Rating
                                            </th>}
                                        

                                            
                                            {year == 2026 && <>
                                                <th
                                                    style={{ textAlign: 'center', cursor: 'pointer' }}
                                                    className={`${sort === "Record" ? "active" : ""}`}
                                                    onClick={() => setSort("Record")}
                                                >
                                                    Proj. Record
                                                </th>

                                                    {!isMobile && <th
                                                        style={{ textAlign: 'center', cursor: 'pointer' }}
                                                        className={`${sort === "Resume" ? "active" : ""} separator`}
                                                        onClick={() => setSort("Resume")}
                                                    >
                                                            Proj.Resume
                                                    </th>}


                                                <th
                                                    style={{ textAlign: 'center', cursor: 'pointer' }}
                                                    className={sort === "Odds" ? "active" : ""}
                                                    onClick={() => setSort("Odds")}
                                                >
                                                    Make Playoff
                                                </th>
                                            </>}
                                        
                                    </>
                                )}
              
                                
            </tr>
          </thead>

          <tbody>
                            {processedTeams.map((team, idx) => {

                                if (idx > max && (conferenceFilter == "Select Conference")) { return null }
                        const regLogo = REG_LOGO.has(team.team) || WHITE_LOGO.has(team.team);
                    const logoSrc = getTeamLogoUrl(listOfTeams[team.team], true);

                    if (filter === 'playoff' && (percent[team.team] || 0) < 0.1) {
                        return null;
                    }

                    const teamIndex = sortedTeams.findIndex((t) => t.team === team.team);
                        const rank = teamIndex !== -1 ? teamIndex + 1 : idx + 1;

                        if(team.team == "Iowa"){
                            console.log(team);
                        }
                    
                    return (
                        <React.Fragment key={team.team}>
                            <tr
                                onClick={() => mSwitch && toggleTeam(team.team)}
                                className={mSwitch ? "has-games will-have-games" : ""}
                            >
                                {!mSwitch && <td>{rank}</td>}

                                <td>
                                    <div className="table-team">
                                        <div className="image-flipper">
                                            <div className="image-container" style={{
        
                                                borderRadius: '3px',
                                            }}>
                                                <img
                                                    src={logoSrc}
                                                    alt={`${team.team} logo`}
                                                    loading="lazy"
      
                                                />
                                            </div>
                                        </div>
                                        <span>&nbsp;{isMobile ? team.abbreviation : team.team}</span>
                                    </div>
                                </td>

                                    <td style={{ textAlign: 'center' }}>{conferenceNames[team?.conference] ?? team?.conference ?? 'N/A'}</td>

                                <td className={(team.LossesTeams || []).some(g => g.game.manual && !g.certain) ? 'special-record' : ''} style={{ textAlign: 'center' }}>
                                        {(() => {
                                            const { wins, losses } = (team.LossesTeams || []).reduce(
                                                (acc, game) => {
                                                    if (game.certain || game.game.manual) {
                                                        if (game.win) acc.wins++;
                                                        else acc.losses++;
                                                    }
                                                    return acc;
                                                },
                                                { wins: 0, losses: 0 }
                                            );

                                            return `${wins}-${losses}`;
                                        })()}
                                </td>

                                {/*<td style={{ textAlign: 'center' }}>
                                    <Lock className="" stroke="#f2a900" strokeWidth={2} size={18} />
                                    {Math.min(
                                        Math.max(0, oddsData[team.team].value),
                                        100
                                    ).toFixed(0)}
                                    </td>*/}



                                {!isMobile && <td title="Log in for access" style={{ textAlign: 'center' }} className="login-td">
                                    {/*                                         <div className="blurry">LOL</div>
 */}                                        {year == 2027 ? <Lock className="lock" stroke="#f2a900" strokeWidth={2} size={18} /> : team.avg.toFixed(1)}

                                    {/*team.avg.toFixed(1)*/}
                                </td>}

                                

                                {year === 2026 && (
                                    <td style={{ textAlign: 'center' }}>
                                        {records?.[team.team] || oddsData[team.team].record}
                                    </td>
                                )}

                                {year === 2026 && !isMobile && (<td style={{ textAlign: 'center' }}>
                                    {/*<Lock className="" stroke="#f2a900" strokeWidth={2} size={18} />*/}

                                        {Math.min(
                                            Math.max(0, ((grades?.[team.team] ? grades[team.team] * 2+50 : oddsData[team.team].resume)) * 1),
                                            100
                                        ).toFixed(0)}
                                    </td>)}

                                {year === 2026 && (
                                    <td style={{ textAlign: 'center' }}>
              
                                        {Object.entries(percent).length > 0 ? (
                                            <div className="td-div">
                                                <span style={{ filter: "opacity(" + (percent[team.team] || 0)/100 + ")" }} className="before"></span>

                                                <span>{getFormattedPlayoffOdds(team.team, percent)}%</span>
                                            </div>
                                        ) : (
                                                <div  className="td-div">
                                                    <span style={{ filter: "opacity(" + oddsData[team.team].odds/100+")"}} className="before"></span>
    
                                                <span>{oddsData[team.team].odds}%</span>
                                                </div>
                                        )
                                        }
                                    </td>
                                )}

                                

                                
                            </tr>

                            
                        </React.Fragment>
                    );
                })}
          </tbody>
        </table>
            )}
            
            </div>
            <br />
            {!mSwitch && max < 200 && (
                <div className="slim-butt" onClick={() => setMax(200)}>View All Teams</div>
            )}
            <br />
            <br />
            <br />
            <br />
            <br />

                 </>
  );
};

export default React.memo(LeaderboardTable);